import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";
import { takeFiles, keepFiles, visibleFiles, humanSize, interpolate, csrfToken } from "./bulk_file_store";

// The page of one bulk upload (15/10/2026):
// - while the people are searched, the progress of the search, then the
//   report once it is done;
// - from the report, the files kept are sent three at a time, each tried
//   again after a cut; the server attaches them in small packets, and both
//   progresses show with the time left;
// - after a reload, the same files are chosen again: only those still to
//   send are taken, by name and size.
export default class extends Controller {
  static targets = [
    "analyzeText", "analyzePercent", "analyzeFill", "elapsed",
    "ready", "startLabel", "repick", "repickResult",
    "progress", "sendSpinner", "sendText", "sendPercent", "sendFill",
    "attachText", "attachPercent", "attachFill", "eta", "pause", "log",
  ];
  static values = { uploadId: Number, status: String, startedAt: Number, statusUrl: String, messages: Object };

  static CONCURRENCY = 3;
  static ATTEMPTS = 4;
  static LOG_SIZE = 8;

  connect() {
    this.queue = [];
    this.paused = false;
    this.sending = false;
    if (this.statusValue === "analyzing") {
      this.tickElapsed();
      this.elapsedTimer = setInterval(() => this.tickElapsed(), 1000);
      this.poll(1000);
    } else if (this.statusValue === "analyzed" || this.statusValue === "uploading") {
      this.prepare();
    }
  }

  disconnect() {
    clearTimeout(this.pollTimer);
    clearInterval(this.elapsedTimer);
    window.removeEventListener("beforeunload", this.warnOnLeave);
  }

  // ── The search ─────────────────────────────────────────────────────────
  tickElapsed() {
    if (!this.hasElapsedTarget) return;
    this.elapsedTarget.textContent = this.duration(Date.now() / 1000 - this.startedAtValue);
  }

  poll(delay) {
    this.pollTimer = setTimeout(() => this.refresh(delay), delay);
  }

  async refresh(delay) {
    let state;
    try {
      const response = await fetch(this.statusUrlValue, { headers: { Accept: "application/json" } });
      state = await response.json();
    } catch (_) {
      return this.poll(delay * 2);
    }
    // A poll carries the counts only: the files to send and their address stay.
    this.state = { ...this.state, ...state };

    if (this.statusValue === "analyzing") {
      if (state.status !== "analyzing") return this.reload();
      const percent = state.items_count ? Math.round((state.analyzed_count * 100) / state.items_count) : 0;
      this.analyzeTextTarget.textContent = interpolate(this.messagesValue.analyzed, { done: state.analyzed_count.toLocaleString(), total: state.items_count.toLocaleString() });
      this.analyzePercentTarget.textContent = `${percent} %`;
      this.analyzeFillTarget.style.width = `${percent}%`;
      return this.poll(delay);
    }

    this.showAttach(state);
    if (state.status === "completed" || state.status === "cancelled") {
      if (!this.sending) return this.reload();
    }
    this.poll(delay);
  }

  reload() {
    window.removeEventListener("beforeunload", this.warnOnLeave);
    Turbo.visit(window.location.href, { action: "replace" });
  }

  // ── The sending ────────────────────────────────────────────────────────
  async prepare() {
    try {
      const response = await fetch(`${this.statusUrlValue}?files=1`, { headers: { Accept: "application/json" } });
      this.state = await response.json();
    } catch (_) {
      return;
    }
    const toSend = this.state.to_send || [];
    if (toSend.length === 0) {
      // Everything is sent: only the attaching is left to follow.
      if (this.state.status === "uploading") {
        this.showProgress();
        this.showSendDone();
        this.poll(2000);
      }
      return;
    }
    this.match(takeFiles(this.uploadIdValue));
    if (this.queue.length > 0) {
      this.showReady();
    } else if (this.hasRepickTarget) {
      this.repickTarget.hidden = false;
    }
  }

  repicked(event) {
    const files = visibleFiles(Array.from(event.target.files || []));
    event.target.value = "";
    this.match(files);
    keepFiles(this.uploadIdValue, files);
    const total = (this.state.to_send || []).length;
    this.repickResultTarget.textContent = this.queue.length
      ? interpolate(this.messagesValue.repick_found, { found: this.queue.length.toLocaleString(), total: total.toLocaleString() })
      : this.messagesValue.repick_none;
    if (this.queue.length > 0) this.showReady();
  }

  // The files still to send, found among those chosen by name and size.
  match(files) {
    const pool = new Map();
    files.forEach((file) => {
      const key = `${file.name}|${file.size}`;
      if (!pool.has(key)) pool.set(key, []);
      pool.get(key).push(file);
    });
    this.queue = (this.state?.to_send || []).flatMap((item) => {
      const file = pool.get(`${item.name}|${item.size}`)?.shift();
      return file ? [{ ...item, file }] : [];
    });
  }

  showReady() {
    if (!this.hasReadyTarget) return;
    this.readyTarget.hidden = false;
    this.startLabelTarget.textContent = interpolate(this.messagesValue.start_send, { count: this.queue.length.toLocaleString() });
  }

  showProgress() {
    if (this.hasReadyTarget) this.readyTarget.hidden = true;
    if (this.hasRepickTarget) this.repickTarget.hidden = true;
    if (this.hasProgressTarget) this.progressTarget.hidden = false;
  }

  async start() {
    if (this.sending || this.queue.length === 0) return;
    this.sending = true;
    this.showProgress();
    this.warnOnLeave = (event) => {
      event.preventDefault();
      event.returnValue = this.messagesValue.leave_warning;
      return this.messagesValue.leave_warning;
    };
    window.addEventListener("beforeunload", this.warnOnLeave);

    this.total = this.queue.length;
    this.totalBytes = this.queue.reduce((sum, item) => sum + item.size, 0);
    this.done = 0;
    this.failed = 0;
    this.lost = 0;
    this.bytesDone = 0;
    this.sendStartedAt = Date.now();
    this.pausedFor = 0;
    this.showSend();
    this.poll(2000);

    const workers = Array.from({ length: this.constructor.CONCURRENCY }, () => this.work());
    await Promise.all(workers);

    this.sending = false;
    window.removeEventListener("beforeunload", this.warnOnLeave);
    this.showSendDone();
    // Files lost to a cut are still to send: the page offers them again.
    if (this.lost > 0) setTimeout(() => this.reload(), 2500);
  }

  async work() {
    while (this.queue.length > 0) {
      await this.waitWhilePaused();
      const item = this.queue.shift();
      if (!item) return;
      await this.sendOne(item);
    }
  }

  async sendOne(item) {
    const url = this.state.upload_url.replace("__ITEM__", item.id);
    for (let attempt = 1; attempt <= this.constructor.ATTEMPTS; attempt += 1) {
      const body = new FormData();
      body.append("file", item.file, item.name);
      try {
        const response = await fetch(url, { method: "POST", headers: { Accept: "application/json", "X-CSRF-Token": csrfToken() }, body });
        if (response.ok) return this.sent(item, true);
        if (response.status < 500) {
          const json = await response.json().catch(() => ({}));
          return this.sent(item, false, json.error);
        }
      } catch (_) {
        // A cut: tried again below.
      }
      if (attempt < this.constructor.ATTEMPTS) {
        this.log(item.name, interpolate(this.messagesValue.file_retry, { attempt: attempt + 1 }), "retry");
        await new Promise((resolve) => setTimeout(resolve, 1500 * attempt));
      }
    }
    this.lost += 1;
    return this.sent(item, false, this.messagesValue.network_error);
  }

  sent(item, ok, error) {
    if (ok) {
      this.done += 1;
      this.bytesDone += item.size;
      this.log(item.name, this.messagesValue.file_ok, "ok");
    } else {
      this.failed += 1;
      this.log(item.name, error || this.messagesValue.file_error, "error");
    }
    this.showSend();
  }

  showSend() {
    const m = this.messagesValue;
    const handled = this.done + this.failed;
    const percent = this.total ? Math.round((handled * 100) / this.total) : 0;
    this.sendTextTarget.textContent = interpolate(m.sent, { done: this.done.toLocaleString(), total: this.total.toLocaleString() }) +
      (this.failed ? ` · ${interpolate(m.sent_failed, { count: this.failed.toLocaleString() })}` : "");
    this.sendPercentTarget.textContent = `${percent} %`;
    this.sendFillTarget.style.width = `${percent}%`;

    const seconds = Math.max((Date.now() - this.sendStartedAt - this.pausedFor) / 1000, 0.5);
    const speed = this.bytesDone / seconds;
    const left = speed > 0 ? (this.totalBytes - this.bytesDone) / speed : null;
    this.etaTarget.textContent = this.bytesDone > 0
      ? interpolate(m.eta, { speed: humanSize(speed), files: (this.done / seconds).toFixed(1), time: left === null ? "…" : this.duration(left) })
      : m.eta_starting;
  }

  showSendDone() {
    const m = this.messagesValue;
    if (this.hasSendSpinnerTarget) this.sendSpinnerTarget.hidden = true;
    if (this.hasPauseTarget) this.pauseTarget.hidden = true;
    if (this.hasSendFillTarget) this.sendFillTarget.style.width = "100%";
    if (this.hasSendPercentTarget) this.sendPercentTarget.textContent = "100 %";
    if (this.hasEtaTarget) this.etaTarget.textContent = m.waiting_attach;
    if (this.hasSendTextTarget && this.total) {
      this.sendTextTarget.textContent = interpolate(m.send_done, { done: this.done.toLocaleString(), total: this.total.toLocaleString() });
    } else if (this.hasSendTextTarget) {
      this.sendTextTarget.textContent = m.send_done_short;
    }
  }

  showAttach(state) {
    if (!this.hasAttachTextTarget) return;
    const counts = state.counts || {};
    const handled = (counts.attached || 0) + (counts.failed || 0);
    const planned = Math.max(state.planned || 0, 1);
    const percent = Math.min(100, Math.round((handled * 100) / planned));
    this.attachTextTarget.textContent = interpolate(this.messagesValue.attached, { done: (counts.attached || 0).toLocaleString(), total: (state.planned || 0).toLocaleString() }) +
      (counts.failed ? ` · ${interpolate(this.messagesValue.attach_failed, { count: counts.failed.toLocaleString() })}` : "");
    this.attachPercentTarget.textContent = `${percent} %`;
    this.attachFillTarget.style.width = `${percent}%`;
  }

  togglePause() {
    this.paused = !this.paused;
    if (this.paused) {
      this.pausedAt = Date.now();
    } else {
      this.pausedFor += Date.now() - this.pausedAt;
      this.resumeWorkers?.forEach((resolve) => resolve());
      this.resumeWorkers = [];
    }
    this.pauseTarget.textContent = this.paused ? this.messagesValue.resume : this.messagesValue.pause;
  }

  waitWhilePaused() {
    if (!this.paused) return Promise.resolve();
    this.resumeWorkers ||= [];
    return new Promise((resolve) => this.resumeWorkers.push(resolve));
  }

  log(name, text, tone) {
    const li = document.createElement("li");
    li.className = `ds-bulk-log__row is-${tone}`;
    const mark = document.createElement("span");
    mark.className = "ds-bulk-log__mark";
    mark.textContent = tone === "ok" ? "✓" : tone === "retry" ? "↻" : "✕";
    const label = document.createElement("span");
    label.className = "ds-bulk-log__name";
    label.textContent = name;
    const note = document.createElement("span");
    note.className = "ds-bulk-log__note";
    note.textContent = text;
    li.append(mark, label, note);
    this.logTarget.prepend(li);
    while (this.logTarget.children.length > this.constructor.LOG_SIZE) this.logTarget.lastChild.remove();
  }

  duration(seconds) {
    const total = Math.max(0, Math.round(seconds));
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const rest = total % 60;
    if (hours) return `${hours} h ${String(minutes).padStart(2, "0")} min`;
    if (minutes) return `${minutes} min ${String(rest).padStart(2, "0")} s`;
    return `${rest} s`;
  }
}
